<template>
  <v-skeleton-loader
    :loading="loading"
    type="article, list-item-two-line@2, actions"
  >
    <v-container class="pt-0">
      <v-row>
        <v-card-title
          class="headline"
          style="color: black"
          :class="$vuetify.breakpoint.xs ? 'pb-2' : ''"
        >
          {{ $t("showTransactions") }}
        </v-card-title>
        <v-chip-group
          :value="1"
          mandatory
          active-class="primary--text"
          :class="$vuetify.breakpoint.xs ? '' : 'py-2'"
          style="margin-left: 8px"
        >
          <v-chip @click="generateChart('day')">{{ $t("last24Hours") }}</v-chip>
          <v-chip @click="generateChart('week')">{{ $t("last7Days") }}</v-chip>
          <v-chip @click="generateChart('month')">{{
            $t("last30Days")
          }}</v-chip>
          <v-chip @click="generateChart('year')">{{
            $t("last12Months")
          }}</v-chip>
        </v-chip-group>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-card
            color="secondary"
            elevation="24"
            style="position: relative; min-height: 350px"
          >
            <div
              style="
                position: absolute;
                top: 0px;
                padding: 16px;
                color: white;
                width: 100%;
              "
            >
              <v-icon color="primary" style="margin-bottom: 8px"
                >mdi-currency-usd-circle-outline</v-icon
              >

              <div class="d-flex justify-space-between">
                <div>
                  <h2 v-if="series[0].data.length !== 0">
                    {{
                      series[0].data
                        .reduce(
                          (r, a) => a.map((b, i) => (r[i] || 0) + b),
                          []
                        )[1]
                        .toFixed(2)
                    }}
                    {{ currencySymbol }}
                  </h2>
                  <span color="white">{{ $t("consumedCredit") }}</span>
                </div>
                <div>
                  <h2>
                    {{ remainingRestaurantCredit.toFixed(2) }}
                    {{ currencySymbol }}
                  </h2>
                  <span color="white">{{ $t("remainingCredit") }}</span>
                </div>
              </div>
            </div>
            <div style="position: absolute; bottom: 0px; width: 100%">
              <apexchart
                height="225"
                type="area"
                :options="chartOptions"
                :series="series"
              ></apexchart>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card>
            <div class="pa-4" style="position: absolute; width: 100%">
              <v-card-title class="pa-0 mb-4">
                {{ $t("topUpRestaurantCredit") }}
                <v-btn
                  @click="addFunds(amount)"
                  style="margin-left: 8px"
                  color="success"
                  :disabled="!valid"
                  text
                  rounded
                  >{{ $t("pay") }}</v-btn
                >
              </v-card-title>
              <v-form ref="form" v-model="valid">
                <v-text-field
                  v-model="amount"
                  :label="$t('value')"
                  type="number"
                  placeholder="100"
                  :hint="`${$t('canTopUpAnyAmount')}. ${$t(
                    'billedInRestaurantsCurrency'
                  )}`"
                  :rules="[
                    (v) => !!v || $t('requiredField'),
                    (v) => Number.isInteger(Number(v)) || $t('mustBeInteger'),
                    (v) => v >= 10 || $t('minimalAmountIs', { amount: 10 }),
                  ]"
                  required
                  persistent-hint
                  rounded
                  filled
                ></v-text-field>
              </v-form>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card elevation="24" flat>
            <v-card-title>{{ $t("transactionHistory") }}</v-card-title>
            <v-data-table
              :headers="headers"
              :header-props="{ sortByText: $t('sortBy') }"
              :items="transactions"
              sort-by="created"
              :sort-desc="true"
            >
              <template v-slot:item.created="{ item }">{{
                new Date(item.created).toLocaleDateString(undefined, {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              }}</template>
              <template v-slot:item.amount="{ item }">
                <v-chip v-if="Number(item.amount) > 0" color="success"
                  >{{ item.amount }} {{ currencySymbol }}</v-chip
                >
                <v-chip v-if="Number(item.amount) < 0" color="error"
                  >{{ item.amount }} {{ currencySymbol }}</v-chip
                >
                <v-chip v-if="Number(item.amount) === 0"
                  >{{ item.amount }} {{ currencySymbol }}</v-chip
                >
              </template>
              <template v-slot:item.invoiceId="{ item }">
                <span v-if="item.invoiceId">{{ item.invoiceIdReadable }}</span>
                <v-btn
                  class="ml-2"
                  v-if="item.invoiceId"
                  icon
                  @click="getInvoice(item.invoiceId, item.invoiceIdReadable)"
                >
                  <v-icon>mdi-file-download</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                {{ $t("noData") }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
      <v-snackbar v-model="snackbar" :color="snackbarColor">
        {{ snackbarText }}
      </v-snackbar>
    </v-container>
  </v-skeleton-loader>
</template>

<script>
import restaurantService from "../services/restaurant.service";
import transactionService from "../services/transaction.service";
import invoiceService from "../services/invoice.service";
import chartService from "../services/chart.service";

export default {
  data() {
    return {
      loading: true,
      stripe: window.Stripe(
        "pk_live_51HxU33DxFtdn9X2RhCJ8BNMHE6sSXiQ7KTKmQHXw6QJ7DiyvQA7bWwPqZOjuvTnMR4D3BvYrsCF9wvVNmHraLu0v00ITZ08n1b"
      ),
      amount: null,
      valid: true,
      snackbar: false,
      snackbarText: null,
      snackbarColor: null,
      remainingRestaurantCredit: 0,
      orderFee: null,
      transactions: [],
      remainingRestaurantCreditChartData: [],
      chartOptions: {
        chart: {
          height: 225,
          type: "area",
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },
        stroke: {
          show: true,
          curve: "smooth",
          lineCap: "round",
          width: 5,
        },
        colors: ["#0360ec"], //#ffaf40
        fill: {
          colors: ["#0360ec"],
          type: "gradient",
          gradient: {
            shade: "dark",
            shadeIntensity: 0.75,
            opacityFrom: 0.55,
            opacityTo: 0.4,
            stops: [5, 90, 100],
          },
        },
        dataLabels: {
          enabled: false, // hodnoty na grafe
        },
        grid: {
          padding: {
            left: 0,
            right: 0,
          },
          show: false,
        },
        yaxis: {
          show: false,
        },
        tooltip: {
          enabled: true,
          x: {
            show: false,
          },
          marker: false,
        },
        xaxis: {
          type: "datetime",
          labels: {
            datetimeUTC: false,
            show: true, // x os- cas
            offsetY: 0,
            style: {
              colors: "#fff",
            },
          },
          axisTicks: {
            show: false,
          },
          tooltip: {
            enabled: false,
          },
          axisBorder: {
            show: false,
          },
        },
      },
      series: [
        {
          name: this.$t("consumedCredit"),
          data: [],
        },
      ],
      headers: [
        {
          text: this.$t("date"),
          value: "created",
        },
        { text: this.$t("value"), value: "amount" },
        { text: this.$t("invoice"), value: "invoiceId", sortable: false },
      ],
    };
  },
  computed: {
    currencySymbol() {
      return localStorage.currencySymbol;
    },
  },
  created() {
    this.fetchData();

    const params = new URLSearchParams(window.location.search);
    if (params.get("status") === "success") {
      // this.snackbarText = `Thanks for topping up your credit. Successfully added ${params.get(
      //   "amount"
      // )}.`;
      this.snackbarText = this.$t("thanksForToppingUp", {
        amount: params.get("amount"),
      });
      this.snackbarColor = "success";
      this.snackbar = true;
    } else if (params.get("status") === "error") {
      // this.snackbarText = `Your payment was not processed correctly.`;
      this.snackbarText = this.$t("paymentFailed");
      this.snackbarColor = "error";
      this.snackbar = true;
    }
  },
  methods: {
    async fetchData() {
      const resp = await transactionService.getTransactions(
        localStorage.getItem("restaurantId")
      );
      this.transactions = resp.transactions;
      this.orderFee = resp.orderFee;
      this.remainingRestaurantCredit = resp.remainingRestaurantCredit;
      this.$emit(
        "on-restaurant-remaining-credit-change",
        this.remainingRestaurantCredit
      );
      this.generateChart("week");
      this.loading = false;
    },
    async generateChart(timePeriod) {
      const remainingRestaurantCreditChartData =
        await chartService.generateChartData(
          this.transactions,
          timePeriod,
          "credits",
          this.orderFee
        );

      this.series = [
        {
          name: this.$t("consumedCredit"),
          data: remainingRestaurantCreditChartData,
        },
      ];
    },
    async addFunds(amount) {
      try {
        gtag("event", "begin_checkout");
      } catch (e) {
        console.log("Tracking disabled");
      }

      const resp = await restaurantService.addFunds(
        localStorage.getItem("restaurantId"),
        Number(amount)
      );

      this.stripe.redirectToCheckout({
        sessionId: resp.sessionId,
      });
    },
    async getInvoice(invoiceId, filename) {
      const resp = await invoiceService.getInvoice(invoiceId, filename);
    },
  },
};
</script>

<style>
.apexcharts-inner .apexcharts-graphical {
  transform: translate(0, -10);
}
</style>
